import type { QueryClient } from "@tanstack/svelte-query";
import pLimit from "p-limit";
import { assign, createMachine, type Sender, type StateFrom } from "xstate";

import { deleteImage, getCurrentUser, updateMetadata } from "services/api";
import {
  hideMimirSearchResult,
  upsertMimirImage,
  upsertMimirSearchResultsWithImage,
} from "state/queries/mimir";
import type { MimirSearchHit } from "services/searchHit";

type DeleteImagesDialogContext = {
  unpublishedItems: MimirSearchHit[];
  publishedItems: MimirSearchHit[];
  deletedItems: MimirSearchHit[];
  hiddenItems: MimirSearchHit[];
  failedItems: MimirSearchHit[];
  errorMessages: Record<string, string>;
  queryClient: QueryClient;
};

type DeleteImagesDialogEvent =
  | { type: "CONFIRM_DELETE" }
  | { type: "CONFIRM_HIDE" }
  | { type: "CANCEL" }
  | { type: "DELETED"; image: MimirSearchHit }
  | { type: "HIDDEN"; image: MimirSearchHit }
  | { type: "FAILED"; image: MimirSearchHit; error: string }
  | { type: "DONE" };

function deleteUnpublishedImages(context: DeleteImagesDialogContext) {
  return async function (send: Sender<DeleteImagesDialogEvent>) {
    const limit = pLimit(5);
    await Promise.all(
      context.unpublishedItems.map((image) =>
        limit(() =>
          deleteImage(image.id)
            .then(() => {
              hideMimirSearchResult(context.queryClient, image.id);
              send({ type: "DELETED", image });
            })
            .catch((err) => {
              send({ type: "FAILED", image, error: err.message });
            }),
        ),
      ),
    );

    send({ type: "DONE" });
  };
}

function deletePublishedImages(context: DeleteImagesDialogContext) {
  return async function (send: Sender<DeleteImagesDialogEvent>) {
    const limit = pLimit(5);
    await Promise.all(
      context.publishedItems.map((image) =>
        limit(() =>
          deleteImage(image.id)
            .then(() => {
              hideMimirSearchResult(context.queryClient, image.id);
              send({ type: "DELETED", image });
            })
            .catch((err) => {
              send({ type: "FAILED", image, error: err.message });
            }),
        ),
      ),
    );

    send({ type: "DONE" });
  };
}

function hidePublishedImages(context: DeleteImagesDialogContext) {
  return async function (send: Sender<DeleteImagesDialogEvent>) {
    const limit = pLimit(5);
    await Promise.all(
      context.publishedItems.map((image) =>
        limit(() =>
          updateMetadata({
            id: image.id,
            visibleTo: { level: "owner", users: [getCurrentUser().id] },
          })
            .then((updatedImage) => {
              upsertMimirImage(context.queryClient, updatedImage);
              upsertMimirSearchResultsWithImage(context.queryClient, updatedImage);
              send({ type: "HIDDEN", image });
            })
            .catch((err) => {
              send({ type: "FAILED", image, error: err.message });
            }),
        ),
      ),
    );
    send({ type: "DONE" });
  };
}

const handleFailed = assign<
  DeleteImagesDialogContext,
  { type: "FAILED"; image: MimirSearchHit; error: string }
>({
  failedItems: (context, event) => [...context.failedItems, event.image],
  errorMessages: (context, event) => ({
    ...context.errorMessages,
    [event.image.id]: event.error,
  }),
});

export type DeleteImageState = StateFrom<typeof deleteImagesMachine>;
export const deleteImagesMachine = createMachine({
  on: {
    CANCEL: "Cancelled",
  },
  initial: "Confirming",
  states: {
    Confirming: {
      on: {
        CONFIRM_DELETE: "DeletingUnpublishedImages",
      },
    },
    ConfirmingPublishedImages: {
      on: {
        CONFIRM_DELETE: "DeletingPublishedImages",
        CONFIRM_HIDE: "HidingPublishedImages",
      },
    },
    DeletingUnpublishedImages: {
      invoke: {
        src: deleteUnpublishedImages,
      },
      on: {
        DELETED: {
          actions: assign<DeleteImagesDialogContext, { type: "DELETED"; image: MimirSearchHit }>({
            deletedItems: (context, event) => [...context.deletedItems, event.image],
          }),
        },
        FAILED: {
          actions: handleFailed,
        },
        DONE: [
          {
            cond: (context) => context.publishedItems.length > 0,
            target: "ConfirmingPublishedImages",
          },
          {
            cond: (context) =>
              context.failedItems.length ===
              context.unpublishedItems.length + context.publishedItems.length,
            target: "Failure",
          },
          { target: "Success" },
        ],
      },
    },
    DeletingPublishedImages: {
      invoke: {
        src: deletePublishedImages,
      },
      on: {
        DELETED: {
          actions: assign<DeleteImagesDialogContext, { type: "DELETED"; image: MimirSearchHit }>({
            deletedItems: (context, event) => [...context.deletedItems, event.image],
          }),
        },
        FAILED: {
          actions: handleFailed,
        },
        DONE: [
          {
            cond: (context) =>
              context.failedItems.length ===
              context.unpublishedItems.length + context.publishedItems.length,
            target: "Failure",
          },
          { target: "Success" },
        ],
      },
    },
    HidingPublishedImages: {
      invoke: {
        src: hidePublishedImages,
      },
      on: {
        HIDDEN: {
          actions: assign<DeleteImagesDialogContext, { type: "HIDDEN"; image: MimirSearchHit }>({
            hiddenItems: (context, event) => [...context.hiddenItems, event.image],
          }),
        },
        FAILED: {
          actions: handleFailed,
        },
        DONE: [
          {
            cond: (context) =>
              context.failedItems.length ===
              context.unpublishedItems.length + context.publishedItems.length,
            target: "Failure",
          },
          { target: "Success" },
        ],
      },
    },
    Failure: { type: "final" },
    Success: { type: "final" },
    Cancelled: { type: "final" },
  },

  schema: {
    context: {} as DeleteImagesDialogContext,
    events: {} as DeleteImagesDialogEvent,
  },
  predictableActionArguments: true,
  preserveActionOrder: true,
});
